/**
 * Riftgate Arena — the pure translation core.
 *
 * Lookup, interpolation and plural selection, with NO state, NO I/O and NO
 * globals. `runtime.ts` owns the singleton that most callers use; everything
 * that decides what a string SAYS lives here, where it can be tested with a
 * literal dictionary and no browser.
 *
 * ── THE DIALECT ─────────────────────────────────────────────────────────────
 * Deliberately identical to the template's server-side dialect
 * (`core/i18n/i18n.service.ts`): nested objects addressed with dots, and
 * `{placeholder}` interpolation. A string fetched from `GET /i18n/:locale`
 * therefore renders here exactly as it renders in an email, and nobody has to
 * remember which side of the wire a message came from.
 *
 * ── PLURALS ARE NOT AN ENGLISH PROBLEM ──────────────────────────────────────
 * A plural message is an OBJECT keyed by CLDR category:
 *
 *     "cards": { "one": "{count} card", "other": "{count} cards" }
 *     "cards": { "one": "{count} карта", "few": "{count} карты",
 *                "many": "{count} карт",  "other": "{count} карты" }
 *
 * The categories that exist are a property of the LANGUAGE, not of us, so the
 * selector is `Intl.PluralRules` and never a hand-rolled `n === 1` — which is
 * wrong for Russian (21 is "one", 22 is "few", 25 is "many"), for Latvian (0 is
 * "zero", and so is 11 and 100) and for Brazilian Portuguese (0 is "one").
 * An exact-count override is spelled `"=0"` and wins over the category, because
 * "No cards yet" is a different sentence from "0 cards", not a different plural.
 */

/** A translated leaf, or a subtree of them. Plural forms are a leaf object. */
export type MessageNode = string | { readonly [segment: string]: MessageNode };

/** A whole locale's messages. Namespaces at the top level. */
export interface Catalogue {
  readonly [segment: string]: MessageNode;
}

export type TParamValue = string | number | boolean | null | undefined;

/**
 * Interpolation parameters. `count` is special: when it is a number and the
 * message is a plural object, it selects the form — and it is interpolated too,
 * so `{count}` works without passing the number twice.
 */
export interface TParams {
  readonly [name: string]: TParamValue;
}

/** One piece of a message split around its placeholders — see `messageParts`. */
export type MessagePart =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'param'; readonly name: string; readonly value: string };

/** `{name}` — same token shape core uses, `\w+` and nothing cleverer. */
const PLACEHOLDER = /\{(\w+)\}/g;

/* ═══════════════════════════════════════════════════════════ lookup ═════ */

/**
 * Walk `a.b.c` through a catalogue. Own properties only — a key of
 * `constructor.prototype` must miss, not return a function.
 */
export function lookupNode(dict: Catalogue | undefined, key: string): MessageNode | undefined {
  if (dict === undefined || key === '') return undefined;
  let node: MessageNode = dict;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    if (!Object.prototype.hasOwnProperty.call(node, segment)) return undefined;
    node = (node as { [k: string]: MessageNode })[segment] as MessageNode;
    if (node === undefined) return undefined;
  }
  return node;
}

/**
 * A message that is present but EMPTY counts as missing.
 *
 * An empty string is what a half-finished translation looks like, and rendering
 * it silently produces a blank button that nobody notices until a player
 * reports it. Treating it as absent sends it down the fallback chain instead.
 */
export function isBlank(value: string): boolean {
  return value.trim() === '';
}

/* ══════════════════════════════════════════════════════════ plurals ═════ */

const pluralRulesCache = new Map<string, Intl.PluralRules>();

/** Cached `Intl.PluralRules`; constructing one is ~100x the cost of using it. */
export function pluralRulesFor(tag: string): Intl.PluralRules {
  const cached = pluralRulesCache.get(tag);
  if (cached !== undefined) return cached;
  let rules: Intl.PluralRules;
  try {
    rules = new Intl.PluralRules(tag);
  } catch {
    // An unknown or malformed tag must not take the UI down with a RangeError.
    rules = new Intl.PluralRules('en');
  }
  pluralRulesCache.set(tag, rules);
  return rules;
}

/** The CLDR categories a language actually uses, e.g. ru -> one/few/many/other. */
export function pluralCategoriesFor(tag: string): readonly Intl.LDMLPluralRule[] {
  return pluralRulesFor(tag).resolvedOptions().pluralCategories;
}

/** A CLDR plural category, or the `=N` exact-count escape. */
const PLURAL_FORM = /^(?:zero|one|two|few|many|other|=\d+)$/;

/**
 * Is this node a set of plural FORMS, rather than a NAMESPACE of messages?
 *
 * The two are both objects, and telling them apart is what stops `has()`
 * promising a message for `settings` (a namespace) that `t()` can only render
 * as the key. A namespace is not a message.
 */
export function isPluralForms(node: MessageNode): node is { [form: string]: MessageNode } {
  if (typeof node !== 'object' || node === null) return false;
  const forms = Object.keys(node);
  return forms.length > 0 && forms.every((form) => PLURAL_FORM.test(form));
}

/**
 * Choose a plural form. `"=N"` beats the category, the category beats `other`,
 * and `other` is the last resort — every CLDR language has one.
 */
export function selectPluralForm(
  forms: { readonly [form: string]: MessageNode },
  count: number,
  tag: string,
): string | undefined {
  const exact = forms[`=${count}`];
  if (typeof exact === 'string' && !isBlank(exact)) return exact;

  const category = pluralRulesFor(tag).select(count);
  const chosen = forms[category];
  if (typeof chosen === 'string' && !isBlank(chosen)) return chosen;

  const other = forms['other'];
  return typeof other === 'string' && !isBlank(other) ? other : undefined;
}

/* ════════════════════════════════════════════════════ interpolation ═════ */

/**
 * Replace `{name}` with `params.name`.
 *
 * A parameter that is absent, null or undefined leaves its token in place. That
 * is deliberate: `Cost: {price}` on screen is a bug report, `Cost: undefined`
 * is a bug report AND a lie, and `Cost: ` is neither — it just looks finished.
 */
export function interpolate(template: string, params?: TParams): string {
  if (params === undefined) return template;
  return template.replace(PLACEHOLDER, (token, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, name)) return token;
    const value = params[name];
    if (value === null || value === undefined) return token;
    return String(value);
  });
}

/**
 * Split a message around its placeholders instead of flattening it to a string.
 *
 * This is how a sentence with a LINK in it gets translated as one sentence:
 *
 *     "accept_label": "I agree to the {terms} and the {privacy}."
 *     messageParts(msg, { terms: 'Terms of Service', privacy: 'Privacy Policy' })
 *
 * gives the caller alternating text and param pieces, so it can append real
 * `<a>` elements between real text nodes. No `innerHTML`, so a translated
 * string can never inject markup, and the word order stays the translator's —
 * which splitting the sentence into `prefix` + `link` + `suffix` keys would
 * quietly take away from every language that does not order it like English.
 */
export function messageParts(template: string, params?: TParams): MessagePart[] {
  const parts: MessagePart[] = [];
  let cursor = 0;
  PLACEHOLDER.lastIndex = 0;
  for (let m = PLACEHOLDER.exec(template); m !== null; m = PLACEHOLDER.exec(template)) {
    const name = m[1] as string;
    const known =
      params !== undefined &&
      Object.prototype.hasOwnProperty.call(params, name) &&
      params[name] !== null &&
      params[name] !== undefined;
    if (!known) continue; // unknown token stays literal text, as `interpolate` does
    if (m.index > cursor) parts.push({ kind: 'text', text: template.slice(cursor, m.index) });
    parts.push({ kind: 'param', name, value: String(params[name]) });
    cursor = m.index + m[0].length;
  }
  if (cursor < template.length) parts.push({ kind: 'text', text: template.slice(cursor) });
  return parts;
}

/* ═══════════════════════════════════════════════════════ resolution ═════ */

/**
 * Resolve one key against ONE catalogue. Returns undefined when the key is
 * absent, blank, or resolves to a subtree that is not a usable message —
 * the caller owns the fallback chain and the missing-key policy.
 */
export function resolveMessage(
  dict: Catalogue | undefined,
  key: string,
  params: TParams | undefined,
  tag: string,
): string | undefined {
  const node = lookupNode(dict, key);
  if (node === undefined) return undefined;

  if (typeof node === 'string') return isBlank(node) ? undefined : node;

  // An object node is only a message if a count picks a form out of it.
  const count = params?.['count'];
  if (typeof count !== 'number' || !Number.isFinite(count)) return undefined;
  return selectPluralForm(node as { [form: string]: MessageNode }, count, tag);
}
